import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, User, ArrowLeft, Facebook, Twitter, Linkedin, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SEOHead } from "@/components/SEOHead";
import BlogSidebar from "@/components/BlogSidebar";

export default function BlogPostChiroThinDoctor() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Why Doctor-Supervised Weight Loss Works Better | Longview, TX"
        description="Struggling to lose weight on your own? Discover why the doctor-supervised ChiroThin program is safer and more effective for East Texas residents."
        ogImage="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/ad300886-b785-4a12-b444-d8cd3efdd8b2.png&w=1280&output=webp&q=80"
        canonical="https://woodsspine.com/blog/chirothin-doctor-supervised"
      />
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-12">
        <Button variant="ghost" className="mb-6 hover:bg-transparent pl-0 text-muted-foreground hover:text-foreground" asChild>
          <Link to="/blog">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Link>
        </Button>
        <div className="lg:grid lg:grid-cols-[1fr_350px] gap-12">
          <div className="space-y-8">
            <header className="space-y-4">
              <Badge className="bg-accent text-white hover:bg-accent/90 border-0 shadow-sm">
                ChiroThin Weight Loss
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight text-primary">
                Why Doctor-Supervised Weight Loss Works Better
              </h1>
              <div className="flex flex-wrap items-center text-sm text-muted-foreground gap-4">
                <Link to="/about#dr-tucker-woods" className="flex items-center hover:text-accent transition-colors">
                  <User className="w-4 h-4 mr-2" />
                  Dr. Tucker Woods
                </Link>
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  June 20, 2026
                </span>
                <span>• 5 min read</span>
              </div>
            </header>

            <img 
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/ad300886-b785-4a12-b444-d8cd3efdd8b2.png&w=1280&output=webp&q=80" 
              alt="Doctor consulting with a patient about the ChiroThin weight loss program in Longview TX" 
              className="w-full rounded-2xl aspect-video object-cover"
              fetchPriority="high"
              decoding="async"
            />

            <article className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-xl font-medium text-foreground">
                Losing weight is rarely as simple as "eat less, move more." If you have tried countless diets only to gain the weight back, it's time to stop guessing and start a clinically proven approach. Discover why the doctor-supervised ChiroThin program provides the safety, accountability, and results that DIY diets lack.
              </p>
              <p>
                The weight loss industry is full of conflicting advice, dangerous supplements, and extreme workout plans. When you attempt to lose weight on your own, you are essentially experimenting on your own metabolism. Without medical oversight, rapid weight loss can lead to muscle wasting, nutritional deficiencies, and a rebound effect where you gain back more fat than you lost.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">The Importance of Medical Supervision</h2>
              <p>
                A doctor-supervised program like <Link to="/longview-chirothin-weight-loss" className="text-accent hover:underline">ChiroThin</Link> is fundamentally different. Before you begin, your doctor conducts a thorough evaluation of your health history, current medications, and specific metabolic challenges. 
              </p>
              <p>
                During the 6-week program, you have regular check-ins with your doctor. This ensures that your body is burning fat for energy, not muscle. The doctor monitors your progress, adjusts your protocol if you hit a plateau, and ensures that your weight loss is occurring at a safe, healthy rate. This level of accountability is often the missing link for patients who struggle to stay consistent on their own.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">How ChiroThin Works</h2>
              <p>
                The ChiroThin program combines a very specific, low-glycemic anti-inflammatory diet with a proprietary nutritional support formula. The ChiroThin drops contain a blend of amino acids, vitamins, and cell salts that are designed to aid in fatty acid transportation, blood sugar stabilization, and metabolism. When combined with the dietary guidelines, the formula helps your body tap into its stored fat reserves for energy, leading to rapid, sustainable weight loss without extreme hunger or fatigue.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Clinical Evidence and Credibility</h2>
              <p>
                The ChiroThin formula is exclusively available through licensed doctors. It is manufactured in the United States in an FDA-certified facility following strict Good Manufacturing Practices (GMP). The program's dietary guidelines are based on sound nutritional science, emphasizing lean proteins, complex carbohydrates, and healthy fats while eliminating highly processed foods and refined sugars that drive systemic inflammation.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Transforming Lives in Longview, TX</h2>
              <p>
                At Woods Spine & Wellness in Longview, TX, Dr. Tucker Woods and Dr. Lance Potter are dedicated to helping our East Texas patients achieve their health goals safely. We don't just hand you a bottle of drops and send you on your way; we guide you through every step of the 6-week journey. We celebrate your victories and help you navigate the challenges. To learn more about how weight loss impacts your overall health, visit our <Link to="/what-we-treat" className="text-accent hover:underline">What We Treat</Link> page.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Frequently Asked Questions</h2>
              
              <div className="space-y-6 mt-6">
                <div className="border-b border-border pb-4">
                  <h3 className="text-lg font-bold text-primary mb-2">Is the ChiroThin program safe if I take medications?</h3>
                  <p className="text-muted-foreground">Because the program is entirely doctor-supervised, we will review all your current medications before you start. The ChiroThin formula is natural and generally safe to use alongside most prescription medications.</p>
                </div>
                <div className="border-b border-border pb-4">
                  <h3 className="text-lg font-bold text-primary mb-2">Will I be starving on this program?</h3>
                  <p className="text-muted-foreground">While the program involves a specific dietary protocol, the ChiroThin drops contain specific amino acids that help stabilize blood sugar and suppress cravings, making the diet much easier to follow than traditional low-calorie plans.</p>
                </div>
                <div className="pb-2">
                  <h3 className="text-lg font-bold text-primary mb-2">How much weight can I expect to lose?</h3>
                  <p className="text-muted-foreground">While individual results vary, many of our patients safely lose between 20 and 35 pounds during the 6-week doctor-supervised program when adhering strictly to the guidelines.</p>
                </div>
              </div>

            </article>

            {/* Social Sharing */}
            <div className="flex items-center gap-4 pt-8 border-t border-border">
              <span className="font-medium text-muted-foreground">Share this article:</span>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Facebook className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Twitter className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Linkedin className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <LinkIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* CTA Block */}
            <div className="bg-secondary/30 p-8 rounded-xl border border-border mt-8">
              <h3 className="text-2xl font-bold text-primary mb-3">Take the guesswork out of weight loss</h3>
              <p className="text-muted-foreground mb-6">
                If you're ready to lose weight safely under the guidance of a doctor, schedule your ChiroThin consultation today.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button className="bg-accent text-white hover:bg-accent/90" asChild>
                  <Link to="/appointment-scheduling">Get Started</Link>
                </Button>
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="text-sm text-muted-foreground border-t border-border pt-6 mt-8">
              Disclaimer: The information provided in this article is for educational purposes only and does not constitute medical advice. Please consult with a healthcare provider before starting any new treatment.
            </div>
          </div>

          <BlogSidebar />
        </div>
      </main>
      <Footer />
    </div>
  );
}
